import React from 'react';
import {
  PlusIcon,
  MessageSquareIcon,
  TrashIcon,
  ClockIcon,
  EditIcon,
  CheckIcon,
  XIcon,
  TrophyIcon,
  UserIcon
} from 'lucide-react';

// Thread Item Component
const ThreadItem = ({
  thread,
  activeThreadId,
  onSelectThread,
  onEditTitle,
  onDeleteThread,
  editingThreadId,
  editingTitle,
  onTitleChange,
  onSaveTitle,
  onCancelEdit,
  isSolo
}) => {
  const isActive = activeThreadId === thread.id;
  const accentColor = isSolo ? 'emerald' : 'cyan';

  return (
    <div
      onClick={() => editingThreadId !== thread.id && onSelectThread(thread.id)}
      className={`p-4 rounded-xl cursor-pointer transition-all group ${
        isActive
          ? `glass border border-${accentColor}-500/50 bg-${accentColor}-500/10 shadow-lg`
          : 'glass border border-slate-700/30 hover:border-slate-600/50 hover:bg-slate-700/30'
      }`}
    >
      {/* Thread Title */}
      <div className="flex items-start justify-between mb-3">
        {editingThreadId === thread.id ? (
          <div className="flex-1 flex items-center gap-2">
            <input
              type="text"
              value={editingTitle}
              onChange={(e) => onTitleChange(e.target.value)}
              className="flex-1 bg-slate-900/70 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              onKeyPress={(e) => e.key === 'Enter' && onSaveTitle()}
              autoFocus
            />
            <button
              onClick={onSaveTitle}
              className="p-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-all border border-green-500/30"
              title="Save"
            >
              <CheckIcon className="h-4 w-4" />
            </button>
            <button
              onClick={onCancelEdit}
              className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-all border border-red-500/30"
              title="Cancel"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <h4 className={`font-bold text-base line-clamp-2 flex-1 ${
              isActive ? (isSolo ? 'text-emerald-300' : 'text-cyan-300') : 'text-white'
            }`}>
              {thread.title}
            </h4>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditTitle(thread.id, thread.title);
                }}
                className="p-1.5 glass border border-slate-600/30 text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 rounded-lg transition-all"
                title="Edit title"
              >
                <EditIcon className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteThread(thread.id);
                }}
                className="p-1.5 glass border border-slate-600/30 text-slate-400 hover:text-red-400 hover:border-red-500/50 rounded-lg transition-all"
                title="Delete thread"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Last Message Preview */}
      {thread.lastMessage && (
        <p className="text-slate-400 text-xs line-clamp-2 mb-3 leading-relaxed italic">
          "{thread.lastMessage}"
        </p>
      )}

      {/* Thread Info */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1 text-slate-500">
          <ClockIcon className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-medium">{new Date(thread.updatedAt).toLocaleDateString()}</span>
        </div>
        <div className={`px-2 py-1 rounded-md font-semibold ${
          isActive
            ? (isSolo ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30')
            : 'bg-slate-700/30 text-slate-400'
        }`}>
          {thread.messageCount} msg{thread.messageCount !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
};

const ThreadSidebar = ({
  threads,
  activeThreadId,
  onSelectThread,
  onNewThread,
  onDeleteThread,
  onEditTitle,
  loading,
  editingThreadId,
  editingTitle,
  onTitleChange,
  onSaveTitle,
  onCancelEdit,
  viewMode // 'tournament' | 'solo'
}) => {
  // Separate threads by mode
  const soloThreads = threads.filter(t => t.mode === 'solo');
  const tournamentThreads = threads.filter(t => t.mode !== 'solo'); // includes 'tournament' and undefined (legacy)

  const isSoloMode = viewMode === 'solo';
  const displayThreads = isSoloMode ? soloThreads : tournamentThreads;

  const commonProps = {
    activeThreadId,
    onSelectThread,
    onEditTitle,
    onDeleteThread,
    editingThreadId,
    editingTitle,
    onTitleChange,
    onSaveTitle,
    onCancelEdit
  };

  return (
    <div className="glass border border-slate-700/50 rounded-2xl p-6 h-full shadow-xl flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-700/50">
        <h3 className="text-xl font-black text-white flex items-center gap-2">
          {isSoloMode ? (
            <>
              <UserIcon className="h-6 w-6 text-emerald-400" />
              Solo Chats
            </>
          ) : (
            <>
              <TrophyIcon className="h-6 w-6 text-cyan-400" />
              Battles
            </>
          )}
        </h3>
        <button
          onClick={onNewThread}
          disabled={loading}
          className={`flex items-center gap-2 text-white px-4 py-2 rounded-xl transition-all disabled:opacity-50 font-bold text-sm shadow-lg ${
            isSoloMode
              ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 hover:shadow-emerald-500/50'
              : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 hover:shadow-cyan-500/50'
          }`}
          title="New Thread"
        >
          <PlusIcon className="h-4 w-4" />
          New
        </button>
      </div>

      {/* Thread List */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-2">
        {/* Current Mode Threads */}
        {displayThreads.length === 0 ? (
          <div className="text-center py-8">
            {isSoloMode ? (
              <UserIcon className="h-10 w-10 text-slate-600/50 mx-auto mb-3" />
            ) : (
              <TrophyIcon className="h-10 w-10 text-slate-600/50 mx-auto mb-3" />
            )}
            <p className="text-slate-400 text-sm font-semibold">
              No {isSoloMode ? 'solo chats' : 'battles'} yet
            </p>
            <p className="text-slate-500 text-xs mt-1">
              {isSoloMode ? 'Start a conversation with an AI' : 'Start a new AI battle!'}
            </p>
          </div>
        ) : (
          displayThreads.map((thread) => (
            <ThreadItem
              key={thread.id}
              thread={thread}
              isSolo={isSoloMode}
              {...commonProps}
            />
          ))
        )}
      </div>

      {/* Footer Info */}
      <div className="mt-4 pt-4 border-t border-slate-700/50">
        <p className="text-slate-400 text-xs text-center leading-relaxed font-medium">
          {isSoloMode
            ? 'Switch between AI agents while keeping context'
            : 'Conversation context maintained across all 3 AI models'}
        </p>
      </div>
    </div>
  );
};

export default ThreadSidebar;